import {CommonModule} from '@angular/common';
import {AfterViewInit, Component, ElementRef, inject, OnInit, signal} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatInputModule} from '@angular/material/input';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatSelectModule} from '@angular/material/select';
import {MatTooltipModule} from '@angular/material/tooltip';
import {TranslateModule} from '@ngx-translate/core';
import {SharedFilterModule, SinglePopupService} from '@progress/kendo-angular-grid';
import {FilterDescriptor, FilterOperator} from '@progress/kendo-data-query';
import {Q9TrimValueDirective} from '@q9elements/ui-kit/common';
import {
  BaseGridFilterComponent,
  DatetimePickerComponent,
  FilterDescriptorWithMetadata,
  GridFilterOperatorSelectComponent,
  IconButtonComponent,
  pickFilterOperators,
  TagsComboboxModule
} from '@q9elements/ui-kit/components';
import {filter} from 'lodash';
import {OperatorsByTypeMapping} from '../../../../models/consts/OperatorByTypeMapping';
import {SingleUseSnakeCaseColumns} from '../../../../models/consts/SingleUseCustomViewColumns';
import {ICustomView} from '../../../../models/custom-view.interface';
import {CustomViewService} from '../../../../services/custom-view.service';

interface CustomFilterDescriptor extends FilterDescriptor {
  list?: string[];
  tags?: string[];
  values?: any[];
}

@Component({
  standalone: true,
  selector: 'app-rme-base-grid-filter',
  templateUrl: './rme-base-grid-filter.component.html',
  imports: [
    CommonModule,
    MatInputModule,
    MatAutocompleteModule,
    FormsModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    Q9TrimValueDirective,
    SharedFilterModule,
    MatSelectModule,
    TranslateModule,
    GridFilterOperatorSelectComponent,
    MatTooltipModule,
    IconButtonComponent,
    TagsComboboxModule,
    DatetimePickerComponent
  ],
  styleUrls: ['./rme-base-grid-filter.component.scss']
})
export class RmeBaseGridFilterComponent
  extends BaseGridFilterComponent
  implements OnInit, AfterViewInit
{
  operators: Array<{
    value: FilterOperator;
    label: string;
  }> | null;
  filterForm: FormGroup;
  private fb: FormBuilder = new FormBuilder();
  public filterItems: FormGroup[] = [];
  public hoveredDeleteIndex: number | null = null;
  public readonly GLOW_ANIMATION_DURATION = 1500;
  public readonly MAX_COMBINED_FILTERS = 10;
  public options: Array<{displayValue: string; value: string}>;
  public filterType: string;
  public config: any = {};

  public readonly tagsConfig = {
    preloadTags: true,
    hideSelected: true,
    selectOnTab: true,
    multiple: true,
    search: true,
    clearSearchOnAdd: true
  };
  public readonly maxTags = 10;

  public readonly newAdded = signal(false);
  public readonly canAddMore = signal(true);
  public readonly editable = signal(false);

  private customViewService = inject(CustomViewService);
  private singlePopupService = inject(SinglePopupService, {optional: true});
  private el = inject(ElementRef);

  ngOnInit(): void {
    this.checkIfCanEditFilter();
    this.filterType = this.column?.filter;
    this.operators = this.resolveOperators();

    this.filterItems = [
      this.fb.group({
        operator: [this.operators[0].value],
        value: [''],
        ...(this.filterType === 'date' ? {secondValue: ''} : {})
      })
    ];

    this.options = this.column?.options;

    super.ngOnInit();
    this.checkIfCanAddMore();
    this.initPopupCloseObserver();
  }

  protected resolveOperators(): Array<{value: FilterOperator; label: string}> {
    return (
      pickFilterOperators(OperatorsByTypeMapping[this.filterType]) ||
      pickFilterOperators([FilterOperator.EqualTo, FilterOperator.NotEqualTo])
    );
  }

  ngAfterViewInit() {
    if (this.editable()) return;

    const parent = this.el.nativeElement.parentElement;
    if (!parent) return;

    const actions = parent.querySelector('.k-actions.k-actions-stretched');
    if (actions) actions.style.display = 'none';
  }

  protected override initFilter(): void {
    this.filterItems = [];
    const currentFilters = filter(this.filter.filters, {
      field: this.field
    }) as FilterDescriptorWithMetadata[];

    if (!currentFilters.length) {
      return void 0;
    }

    currentFilters.forEach(filter => {
      const {operator, list, tags} = filter as CustomFilterDescriptor;
      let {value} = filter;

      if (operator || value) {
        if (this.filterType === 'tags' && tags?.length) {
          value = [...tags];
        }
        if (this.filterType === 'predefinedList' && list?.length) {
          value = [...list];
        }
        this.filterItems.push(
          this.fb.group({
            operator: [{value: <FilterOperator>operator, disabled: !this.editable()}],
            value: [{value, disabled: !this.editable()}]
          })
        );
      }
    });
  }

  protected override initFilterChangesObserver() {
    this.filterItems.forEach((filterItem, idx) => {
      filterItem.valueChanges
        .pipe(this.takeUntilComponentDestroy())
        .subscribe(({operator, value}) => {
          this.applyFilter(value, <FilterOperator>operator, idx);
        });
    });
  }

  protected initPopupCloseObserver() {
    this.singlePopupService.onClose.subscribe(e => {
      e.preventDefault();
    });
  }

  protected override applyFilter(
    value: string,
    operator: FilterOperator | FilterOperator[],
    idx: number
  ) {
    if (idx !== undefined) {
      this.filter.filters[idx] = {
        field: this.field,
        operator: operator,
        value,
        list: this.filterType === 'predefinedList' ? [...value] : undefined,
        tags: this.filterType === 'tags' ? [...value] : undefined
      } as CustomFilterDescriptor;
    } else {
      super.applyFilter(value, operator);
    }
  }

  public addCombined() {
    if (!this.editable()) {
      return;
    }
    this.filterItems.push(
      this.fb.group({
        operator: [FilterOperator.EqualTo],
        value: ['']
      })
    );

    this.glowNew();
    this.initFilterChangesObserver();
    this.checkIfCanAddMore();
  }

  public checkIfCanAddMore() {
    const {length} = this.filterItems;
    const isMultipleAllowed =
      !SingleUseSnakeCaseColumns.includes(this.field) &&
      this.editable() &&
      (this.options?.length
        ? length < this.options.length && length < this.MAX_COMBINED_FILTERS
        : length < this.MAX_COMBINED_FILTERS);
    this.canAddMore.set(isMultipleAllowed);
  }

  protected checkIfCanEditFilter(): void {
    const {columns} = this.customViewService.selectedView as ICustomView;
    const currentColumn = columns.find(col => col.value === this.field);
    this.editable.set(!currentColumn.disableFilter);
  }

  public removeFilter(index: number) {
    this.filter.filters.splice(index, 1);
    this.filterItems.splice(index, 1);
    this.filterService.filter(this.filter);
    this.checkIfCanAddMore();
  }

  private glowNew() {
    this.newAdded.set(true);
    setTimeout(() => {
      this.newAdded.set(false);
    }, this.GLOW_ANIMATION_DURATION);
  }

  public updateTags(event) {
    this.config.tagsList = event;
  }
}
